import React, { useRef, useState, useEffect } from "react";

import {
  StyledChoose,
  StyledFlexContainer,
  StyledLabel,
  StyledRadioOption,
  StyledVerticalContainer,
} from "./Styles";
import { useResizeObserver, useForkRef } from "../../utils";
import { ButtonGroup } from "../ButtonGroup";
import { Checkbox } from "../Checkbox";
import { RadioButton } from "../RadioButton";

export interface ChooseV2Option {
  id: string | number;
  name: string;
  disabled?: boolean;
}

export interface ChooseV2Props {
  required?: boolean;
  multiple?: boolean;
  invalid?: boolean;
  options: ChooseV2Option[];
  selected: (string | number)[];
  onChange: (ids: (string | number)[]) => void;
}

export const ChooseV2 = React.forwardRef<
  HTMLDivElement,
  ChooseV2Props &
    Omit<React.ComponentPropsWithoutRef<"div">, keyof ChooseV2Props>
>(
  (
    {
      required = false,
      multiple = false,
      invalid = false,
      options,
      selected,
      onChange,
      ...rest
    },
    ref
  ) => {
    const internalRef = useRef<HTMLDivElement | null>(null);
    const flexContainerRef = useRef<HTMLDivElement | null>(null);
    const handleRef = useForkRef(ref, internalRef);

    const [useVerticalLayout, setUseVerticalLayout] = useState(false);
    const [isReady, setIsReady] = useState(false);

    const parentSize = useResizeObserver(internalRef);
    const flexSize = useResizeObserver(flexContainerRef);

    const parentWidth = parentSize?.width ?? 0;
    const flexWidth = flexSize?.width ?? 0;

    useEffect(() => {
      if (parentWidth === 0) return;

      const measure = () => {
        const temp = document.createElement("div");
        temp.style.display = "inline-flex";
        temp.style.visibility = "hidden";
        temp.style.position = "absolute";
        temp.style.whiteSpace = "nowrap";
        temp.style.pointerEvents = "none";
        document.body.appendChild(temp);

        options.forEach((option) => {
          const button = document.createElement("button");
          button.textContent = option.name;
          button.style.padding = "6px 12px";
          button.style.border = "1px solid transparent";
          button.style.marginRight = "4px";
          temp.appendChild(button);
        });

        const totalWidth = temp.offsetWidth + 8;
        document.body.removeChild(temp);

        setUseVerticalLayout(totalWidth > parentWidth);
        setIsReady(true);
      };

      requestAnimationFrame(measure);
    }, [parentWidth, options, flexWidth]);

    const handleSelect = (id: string | number) => {
      if (multiple) {
        const newSelected = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id];

        if (required && newSelected.length === 0) return;
        onChange(newSelected);
      } else {
        const newSelected = selected.includes(id) ? [] : [id];
        if (required && newSelected.length === 0) return;
        onChange(newSelected);
      }
    };

    if (!isReady) {
      return (
        <div ref={handleRef} style={{ width: "100%", minHeight: "32px" }} />
      );
    }

    return (
      <div ref={handleRef} {...rest} style={{ display: "flex", width: "100%" }}>
        {useVerticalLayout ? (
          <StyledVerticalContainer>
            {options.map((option) => (
              <StyledRadioOption
                $disabled={!!option.disabled}
                key={option.id}
                data-disabled={option.disabled}
              >
                {multiple || !required ? (
                  <Checkbox
                    checked={selected.includes(option.id)}
                    disabled={option.disabled}
                    onChange={() => !option.disabled && handleSelect(option.id)}
                    name={`choose-v2-${option.id}`}
                    id={`choose-v2-${option.id}`}
                  />
                ) : (
                  <RadioButton
                    checked={selected.includes(option.id)}
                    disabled={option.disabled}
                    onChange={() => !option.disabled && handleSelect(option.id)}
                    id={`choose-v2-${option.id}`}
                    name={`choose-v2-${option.id}`}
                  />
                )}
                <StyledLabel
                  $disabled={!!option.disabled}
                  weight="regular"
                  htmlFor={`choose-v2-${option.id}`}
                >
                  {option.name}
                </StyledLabel>
              </StyledRadioOption>
            ))}
          </StyledVerticalContainer>
        ) : (
          <StyledFlexContainer ref={flexContainerRef}>
            <ButtonGroup invalid={invalid}>
              {options.map((option) => (
                <StyledChoose
                  key={option.id}
                  active={selected.includes(option.id)}
                  disabled={option.disabled}
                  onClick={() => handleSelect(option.id)}
                >
                  {option.name}
                </StyledChoose>
              ))}
            </ButtonGroup>
          </StyledFlexContainer>
        )}
      </div>
    );
  }
);

ChooseV2.displayName = "ChooseV2";
